import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { orderApi } from '@formosa/shared/api/order';
import { buildTradePath } from '@formosa/shared/utils/productNavigation';
import {
  formatThumbChange,
  formatThumbPrice,
  normalizeSymbolCode,
} from '@formosa/shared/utils/marketThumb';
import HomeHeader from '../../components/home/HomeHeader';
import useProductSearch from '../../context/ProductSearchContext';
import useMarketThumbSocket from '../../hooks/useMarketThumbSocket';
import useRequireAuth from '../../hooks/useRequireAuth';
import { useTranslation } from '../../i18n/LocaleContext';
import { memberApi } from '@formosa/shared/api/member';
import { formatDateTime } from '@formosa/shared/utils/formatDateTime';
import { resolveMemberMediaUrl } from '@formosa/shared/utils/mediaUrl';
import { resolveNewsApiLang } from '@formosa/shared/utils/newsLang';
import {
  HOME_QUICK_ACTIONS,
} from '../../constants/homeContent';

export default function HomePage() {
  const navigate = useNavigate();
  const { locale, t } = useTranslation();
  const { openSearch } = useProductSearch();
  const requireAuth = useRequireAuth();
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [carouselSlides, setCarouselSlides] = useState([]);
  const [dynamicMarqueeItems, setDynamicMarqueeItems] = useState([]);
  const [generalMarqueeItems, setGeneralMarqueeItems] = useState([]);
  const [generalMarqueeIndex, setGeneralMarqueeIndex] = useState(0);
  const [hotProducts, setHotProducts] = useState([]);
  const [newsItems, setNewsItems] = useState([]);
  const [aboutContent, setAboutContent] = useState(null);
  const [beginnerGuideBody, setBeginnerGuideBody] = useState('');

  const dynamicMarqueeText = useMemo(
    () => dynamicMarqueeItems.map((item) => item.title).filter(Boolean).join('　'),
    [dynamicMarqueeItems]
  );

  // 熱門區：依 listHotProducts 回傳的 symbol 動態訂閱
  const hotSymbolCodes = useMemo(
    () => hotProducts.map((product) => product.marketSymbolCode).filter(Boolean),
    [hotProducts]
  );
  const hotThumbs = useMarketThumbSocket(hotSymbolCodes);

  useEffect(() => {
    if (generalMarqueeItems.length <= 1) {
      setGeneralMarqueeIndex(0);
      return undefined;
    }
    const timer = setInterval(() => {
      setGeneralMarqueeIndex((prev) => (prev + 1) % generalMarqueeItems.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [generalMarqueeItems.length]);

  useEffect(() => {
    if (carouselSlides.length <= 1) {
      return undefined;
    }
    const timer = setInterval(() => {
      setCarouselIndex((prev) => (prev + 1) % carouselSlides.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [carouselSlides.length]);

  useEffect(() => {
    let cancelled = false;

    async function loadCarousel() {
      try {
        const items = await memberApi.carouselList({ lang: resolveNewsApiLang(locale) });
        if (!cancelled) {
          const slides = Array.isArray(items) ? items.filter((item) => item?.image) : [];
          setCarouselSlides(slides);
          setCarouselIndex(0);
        }
      } catch {
        if (!cancelled) {
          setCarouselSlides([]);
          setCarouselIndex(0);
        }
      }
    }

    loadCarousel();
    return () => {
      cancelled = true;
    };
  }, [locale]);

  useEffect(() => {
    let cancelled = false;
    const apiLang = resolveNewsApiLang(locale);

    async function loadMarquees() {
      try {
        const [dynamicItems, generalItems] = await Promise.all([
          memberApi.dynamicMarqueeList({ lang: apiLang }),
          memberApi.marqueeList({ lang: apiLang }),
        ]);
        if (!cancelled) {
          setDynamicMarqueeItems(Array.isArray(dynamicItems) ? dynamicItems : []);
          setGeneralMarqueeItems(Array.isArray(generalItems) ? generalItems : []);
          setGeneralMarqueeIndex(0);
        }
      } catch {
        if (!cancelled) {
          setDynamicMarqueeItems([]);
          setGeneralMarqueeItems([]);
          setGeneralMarqueeIndex(0);
        }
      }
    }

    loadMarquees();
    return () => {
      cancelled = true;
    };
  }, [locale]);

  useEffect(() => {
    let cancelled = false;

    async function loadHotCoins() {
      try {
        const products = await orderApi.listHotProducts(4);
        const rows = Array.isArray(products) ? products : [];
        if (cancelled) {
          return;
        }
        setHotProducts(rows);
      } catch {
        if (!cancelled) {
          setHotProducts([]);
        }
      }
    }

    loadHotCoins();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadNews() {
      try {
        const items = await memberApi.newsLatest({ lang: resolveNewsApiLang(locale), limit: 3 });
        if (!cancelled) {
          setNewsItems(Array.isArray(items) ? items : []);
        }
      } catch {
        if (!cancelled) {
          setNewsItems([]);
        }
      }
    }

    loadNews();
    return () => {
      cancelled = true;
    };
  }, [locale]);

  useEffect(() => {
    let cancelled = false;

    async function loadAbout() {
      try {
        const data = await memberApi.aboutUs({ lang: resolveNewsApiLang(locale) });
        if (!cancelled) {
          setAboutContent(data);
        }
      } catch {
        if (!cancelled) {
          setAboutContent(null);
        }
      }
    }

    loadAbout();
    return () => {
      cancelled = true;
    };
  }, [locale]);

  useEffect(() => {
    let cancelled = false;

    async function loadBeginnerGuide() {
      try {
        const data = await memberApi.beginnerGuide({ lang: resolveNewsApiLang(locale) });
        if (!cancelled) {
          setBeginnerGuideBody(data?.body?.trim() || '');
        }
      } catch {
        if (!cancelled) {
          setBeginnerGuideBody('');
        }
      }
    }

    loadBeginnerGuide();
    return () => {
      cancelled = true;
    };
  }, [locale]);

  function handleQuickAction(action) {
    if (action.auth) {
      requireAuth(action.path);
      return;
    }
    if (action.path) {
      navigate(action.path);
      return;
    }
    window.alert('功能即將上線');
  }

  return (
    <div className="home-page">
      <HomeHeader />

      {dynamicMarqueeText ? (
        <div className="home-marquee home-marquee--star">
          <span className="home-marquee__icon" aria-hidden>★</span>
          <div className="home-marquee__track">
            <span>{dynamicMarqueeText}</span>
            <span aria-hidden>{dynamicMarqueeText}</span>
          </div>
        </div>
      ) : null}

      {carouselSlides.length > 0 ? (
        <section className="home-carousel" aria-label="輪播圖">
          <div className="home-carousel__slide">
            <img
              src={resolveMemberMediaUrl(carouselSlides[carouselIndex]?.image)}
              alt=""
              className="home-carousel__image"
            />
          </div>
          {carouselSlides.length > 1 ? (
            <div className="home-carousel__dots">
              {carouselSlides.map((slide, index) => (
                <button
                  key={slide.id}
                  type="button"
                  className={`home-carousel__dot ${index === carouselIndex ? 'is-active' : ''}`}
                  aria-label={`第 ${index + 1} 張`}
                  onClick={() => setCarouselIndex(index)}
                />
              ))}
            </div>
          ) : null}
        </section>
      ) : null}

      {generalMarqueeItems.length > 0 ? (
        <div className="home-marquee home-marquee--announce">
          <span className="home-marquee__icon" aria-hidden>📣</span>
          <div className="home-marquee__vertical">
            <div
              className="home-marquee__vertical-track"
              style={{
                transform: `translateY(-${(generalMarqueeIndex * 100) / generalMarqueeItems.length}%)`,
              }}
            >
              {generalMarqueeItems.map((item) => (
                <span key={item.id} className="home-marquee__vertical-item">
                  {item.title}
                </span>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      <button
        type="button"
        className="home-deposit-cta"
        onClick={() => requireAuth('/deposit')}
      >
        <span className="home-deposit-cta__icon" aria-hidden>₿</span>
        <span className="home-deposit-cta__text">
          <strong>入款</strong>
          <small>支援BTC, USDT, ETH等</small>
        </span>
        <span className="home-deposit-cta__arrow" aria-hidden>›</span>
      </button>

      <section className="home-quick-grid" aria-label="快捷功能">
        {HOME_QUICK_ACTIONS.map((action) => (
          <button
            key={action.key}
            type="button"
            className="home-quick-grid__item"
            onClick={() => handleQuickAction(action)}
          >
            <span className={`home-quick-grid__icon home-quick-grid__icon--${action.icon}`} />
            <span>{action.label}</span>
          </button>
        ))}
      </section>

      <section className="home-section">
        <div className="home-section__header">
          <h2>🔥 熱門</h2>
        </div>
        <ul className="home-coin-list">
          {hotProducts.map((product) => {
            const code = normalizeSymbolCode(product.marketSymbolCode);
            const thumb = hotThumbs[code];
            const change = formatThumbChange(thumb);
            const symbolCode = product.displayName || product.marketSymbolCode;
            const pairLabel = product.name || product.marketSymbolCode;
            return (
              <li key={product.id}>
                <button
                  type="button"
                  className="home-coin-list__item"
                  onClick={() => navigate(buildTradePath(product.playType || 'FUTURES', product.marketSymbolCode))}
                >
                  <div className="home-coin-list__left">
                    <span className="home-coin-list__badge">{symbolCode?.slice(0, 1)}</span>
                    <div>
                      <div className="home-coin-list__symbol">{symbolCode}</div>
                      <div className="home-coin-list__pair">{pairLabel}</div>
                    </div>
                  </div>
                  <div className="home-coin-list__right">
                    <div className="home-coin-list__price">{formatThumbPrice(thumb?.price, 2)}</div>
                    <div className={`home-coin-list__change ${change.positive ? 'is-up' : 'is-down'}`}>
                      {change.text}
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
        <button type="button" className="home-section__more" onClick={openSearch}>
          查看更多
        </button>
      </section>

      <section className="home-section">
        <div className="home-section__header">
          <h2>🌐 {t('news.title')}</h2>
          <Link to="/news" className="home-section__link">{t('news.more')}</Link>
        </div>
        <ul className="home-news-list">
          {newsItems.length === 0 ? (
            <li className="home-news-list__empty">{t('news.empty')}</li>
          ) : (
            newsItems.map((item) => (
              <li key={item.id}>
                <Link to={`/news/${item.id}`} className="home-news-list__item">
                  <div className="home-news-list__title">{item.title}</div>
                  <div className="home-news-list__time">{formatDateTime(item.newsTime)}</div>
                </Link>
              </li>
            ))
          )}
        </ul>
      </section>

      {beginnerGuideBody ? (
        <section className="home-section home-section--beginner-guide">
          <div className="home-section__header">
            <h2>{t('beginnerGuide.title')}</h2>
          </div>
          <div
            className="home-beginner-guide__html"
            dangerouslySetInnerHTML={{ __html: beginnerGuideBody }}
          />
        </section>
      ) : null}

      {aboutContent?.image || aboutContent?.video ? (
        <section className="home-section home-section--about">
          <div className="home-section__header">
            <h2>{t('aboutUs.title')}</h2>
          </div>
          {aboutContent?.image ? (
            <img src={resolveMemberMediaUrl(aboutContent.image)} alt="" className="home-about__image" />
          ) : null}
          {aboutContent?.video ? (
            <video
              src={resolveMemberMediaUrl(aboutContent.video)}
              controls
              playsInline
              className="home-about__video"
            />
          ) : null}
        </section>
      ) : null}
    </div>
  );
}
